import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { X } from "lucide-react";

interface RenameTopicModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentTitle: string;
  currentInstructions?: string;
  onConfirm: (newTitle: string, newInstructions: string) => void;
  isLoading?: boolean;
}

export const RenameTopicModal = ({
  open,
  onOpenChange,
  currentTitle,
  currentInstructions = "",
  onConfirm,
  isLoading = false
}: RenameTopicModalProps) => {
  const [title, setTitle] = useState(currentTitle);
  const [instructions, setInstructions] = useState(currentInstructions);

  const handleConfirm = () => {
    if (title.trim()) {
      onConfirm(title.trim(), instructions.trim());
    }
    onOpenChange(false);
  };

  const handleCancel = () => {
    setTitle(currentTitle);
    setInstructions(currentInstructions);
    onOpenChange(false);
  };

  const handleClear = () => {
    setTitle("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-8 bg-white/95 backdrop-blur-md border border-gray-200/50 shadow-xl" hideCloseButton>
        <DialogHeader className="sr-only">
          <DialogTitle>Update Topic</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-8">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-xl font-medium text-gray-900 mb-2 text-left">Update Topic</h2>
            <p className="text-gray-600 text-sm text-left">
              Update the topic title and instructions
            </p>
          </div>

          {/* Form */}
          <div className="space-y-6">
            <div>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter title"
                className="w-full h-10 px-4 bg-slate-100 border border-slate-200 rounded-lg text-gray-800"
                disabled={isLoading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    handleConfirm();
                  } else if (e.key === 'Escape') {
                    handleCancel();
                  }
                }}
              />
            </div>
            
            <div>
              <Textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Enter instructions (optional)"
                className="w-full px-4 py-3 bg-slate-100 border border-slate-200 rounded-lg text-gray-800 resize-none"
                rows={3}
                disabled={isLoading}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    handleCancel();
                  }
                }}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
                className="h-12 px-8 border border-gray-200 hover:bg-gray-50 bg-white rounded-lg shadow-none"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={isLoading || !title.trim()}
                className="h-12 px-8 text-white font-medium rounded-lg border-0 shadow-none bg-slate-900 hover:bg-slate-800"
              >
                {isLoading ? "Updating..." : "Update Topic"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};